/**
 * MessagesWidget Component
 *
 * Виджет для управления сообщениями организации.
 * Поддерживает: WhatsApp, Telegram, Viber, VK, Email
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Group,
  Text,
  Button,
  Box,
  Stack,
  ActionIcon,
  Menu,
  Loader,
  Center,
  Badge,
} from '@mantine/core';
import {
  IconMessage,
  IconPlus,
  IconDots,
  IconPencil,
  IconTrash,
  IconClock,
  IconUser,
  IconEdit,
  IconBrandWhatsapp,
  IconBrandTelegram,
  IconBrandVk,
  IconPhone,
  IconMail,
} from '@tabler/icons-react';
import BaseWidget from './BaseWidget';
import MessageFormModal from './MessageFormModal';
import MessageViewModal from './MessageViewModal';
import EditReasonModal from './EditReasonModal';
import tokens from '../../../theme/designTokens';

/**
 * Иконки и метаданные мессенджеров
 */
const MESSENGER_CONFIG = {
  whatsapp: { icon: IconBrandWhatsapp, color: 'green', label: 'WhatsApp' },
  telegram: { icon: IconBrandTelegram, color: 'blue', label: 'Telegram' },
  viber: { icon: IconPhone, color: 'grape', label: 'Viber' },
  vk: { icon: IconBrandVk, color: 'indigo', label: 'VK' },
  email: { icon: IconMail, color: 'cyan', label: 'Email' },
};

/**
 * API функции для сообщений
 */
const messagesApi = {
  async getMessages(organizationId) {
    const response = await fetch(`/api/organizations/${organizationId}/messages`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить сообщения');
    }
    return response.json();
  },

  async createMessage(organizationId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось создать сообщение');
    }
    return response.json();
  },

  async updateMessage(organizationId, messageId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/messages/${messageId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось обновить сообщение');
    }
    return response.json();
  },

  async deleteMessage(organizationId, messageId) {
    const response = await fetch(`/api/organizations/${organizationId}/messages/${messageId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить сообщение');
    }
  },

  async getTemplate(channel) {
    const response = await fetch(`/api/message-templates/by-channel/${channel}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      return null;
    }
    return response.json();
  },
};

/**
 * Форматирование имени пользователя
 */
const formatUserName = (user) => {
  if (!user) return 'Неизвестный';
  if (user.full_name) return user.full_name;
  return user.email;
};

/**
 * Форматирование даты и времени
 */
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Форматирование контакта (телефон)
 */
const formatContact = (contact, messengerType) => {
  if (!contact) return '';

  // Для телефонных мессенджеров форматируем как телефон
  if (['whatsapp', 'viber'].includes(messengerType)) {
    // Удаляем все кроме цифр
    const digits = contact.replace(/\D/g, '');
    if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
      // Российский номер
      return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
    }
    if (digits.length === 10) {
      return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
    }
  }

  return contact;
};

/**
 * Компонент отображения сообщения
 */
const MessageItem = ({ message, onView, onEdit, onDelete }) => {
  const messengerConfig = MESSENGER_CONFIG[message.messenger_type];
  const MessengerIcon = messengerConfig?.icon || IconMessage;
  const senderName = formatUserName(message.sender);

  return (
    <Group
      p="sm"
      style={{
        borderRadius: '12px',
        backgroundColor: tokens.colors.neutral[1],
        transition: 'background-color 150ms ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[2])}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[1])}
      onClick={() => onView(message)}
    >
      {/* Иконка мессенджера */}
      <Box
        style={{
          width: 40,
          height: 40,
          borderRadius: '10px',
          backgroundColor: messengerConfig?.color === 'green' ? '#dcfce7' :
                          messengerConfig?.color === 'blue' ? '#dbeafe' :
                          messengerConfig?.color === 'grape' ? '#f3e8ff' :
                          messengerConfig?.color === 'indigo' ? '#e0e7ff' :
                          messengerConfig?.color === 'cyan' ? '#cffafe' : '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MessengerIcon
          size={20}
          color={messengerConfig?.color === 'green' ? '#16a34a' :
                messengerConfig?.color === 'blue' ? '#2563eb' :
                messengerConfig?.color === 'grape' ? '#9333ea' :
                messengerConfig?.color === 'indigo' ? '#4f46e5' :
                messengerConfig?.color === 'cyan' ? '#0891b2' : '#6b7280'}
        />
      </Box>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Badge
            size="xs"
            color={messengerConfig?.color || 'gray'}
            variant="light"
            radius="xl"
          >
            {messengerConfig?.label || message.messenger_type}
          </Badge>
          <Text size="sm" fw={500} truncate>
            {formatContact(message.messenger_contact, message.messenger_type)}
          </Text>
          {/* Бейдж типа сообщения */}
          <Badge
            size="xs"
            color={message.message_type === 'primary' ? 'green' : 'yellow'}
            variant="filled"
            radius="xl"
          >
            {message.message_type === 'primary' ? 'Первичное' : 'Вторичное'}
          </Badge>
          {/* Бейдж редактирования */}
          {message.is_edited && (
            <Badge
              size="xs"
              color="orange"
              variant="filled"
              radius="xl"
              leftSection={<IconEdit size={10} />}
            >
              Изменено{message.edit_count > 1 ? ` (${message.edit_count})` : ''}
            </Badge>
          )}
        </Group>

        <Group gap="md" mt={4}>
          <Text size="xs" c="dimmed">
            <Group gap={4}>
              <IconClock size={12} />
              {formatDateTime(message.sent_datetime)}
            </Group>
          </Text>

          <Text size="xs" c="dimmed">
            <Group gap={4}>
              <IconUser size={12} />
              {senderName}
            </Group>
          </Text>
        </Group>

        {message.message_text && (
          <Text size="xs" c="dimmed" mt={4} lineClamp={2}>
            {message.message_text}
          </Text>
        )}
      </Box>

      {/* Меню действий */}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
          <Menu.Item
            leftSection={<IconPencil size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(message);
            }}
          >
            Редактировать
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconTrash size={14} />}
            color="red"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(message);
            }}
          >
            Удалить
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};

/**
 * Основной компонент виджета
 */
const MessagesWidget = ({ organization, onActivityChange }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Модальное окно формы
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);

  // Модальное окно просмотра
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingMessage, setViewingMessage] = useState(null);

  // Модальное окно причины редактирования
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [pendingEditData, setPendingEditData] = useState(null);

  // Загрузка сообщений
  const loadMessages = useCallback(async () => {
    if (!organization?.id) return;

    try {
      setLoading(true);
      const data = await messagesApi.getMessages(organization.id);
      setMessages(data.items || []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Открыть модальное окно для добавления
  const handleAddMessage = () => {
    setEditingMessage(null);
    setModalOpen(true);
  };

  // Открыть модальное окно для просмотра
  const handleViewMessage = (message) => {
    setViewingMessage(message);
    setViewModalOpen(true);
  };

  // Открыть модальное окно для редактирования
  const handleEditMessage = (message) => {
    setViewModalOpen(false);
    setViewingMessage(null);
    setEditingMessage(message);
    setModalOpen(true);
  };

  // Удалить сообщение
  const handleDeleteMessage = async (message) => {
    const messengerLabel = MESSENGER_CONFIG[message.messenger_type]?.label || message.messenger_type;
    if (!window.confirm(`Удалить сообщение ${messengerLabel}?`)) {
      return;
    }

    try {
      await messagesApi.deleteMessage(organization.id, message.id);
      await loadMessages();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to delete message:', err);
      alert(err.message);
    }
  };

  // Сохранить сообщение
  const handleSubmit = async (data) => {
    // Если это редактирование, сначала спрашиваем причину
    if (editingMessage) {
      setPendingEditData(data);
      setModalOpen(false);
      setReasonModalOpen(true);
      return;
    }

    // Создание нового сообщения
    setActionLoading(true);
    try {
      await messagesApi.createMessage(organization.id, data);
      setModalOpen(false);
      setEditingMessage(null);
      await loadMessages();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  // Подтверждение редактирования с причиной
  const handleConfirmEdit = async (reason) => {
    if (!editingMessage || !pendingEditData) return;

    setActionLoading(true);
    try {
      const dataWithReason = {
        ...pendingEditData,
        edit_reason: reason,
      };
      await messagesApi.updateMessage(organization.id, editingMessage.id, dataWithReason);
      setReasonModalOpen(false);
      setPendingEditData(null);
      setEditingMessage(null);
      await loadMessages();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to update message:', err);
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Функция загрузки шаблона
  const getTemplate = async (channel) => {
    return messagesApi.getTemplate(channel);
  };

  return (
    <>
      <BaseWidget
        title="Сообщения"
        icon={IconMessage}
        count={messages.length}
        collapsible={true}
        defaultExpanded={messages.length > 0}
        actions={
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              handleAddMessage();
            }}
          >
            Добавить
          </Button>
        }
      >
        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : messages.length > 0 ? (
          <Stack gap="xs">
            {messages.map((message) => (
              <MessageItem
                key={message.id}
                message={message}
                onView={handleViewMessage}
                onEdit={handleEditMessage}
                onDelete={handleDeleteMessage}
              />
            ))}
          </Stack>
        ) : (
          <Box py="lg" style={{ textAlign: 'center' }}>
            <IconMessage
              size={40}
              style={{ color: tokens.colors.neutral[4], marginBottom: '8px' }}
            />
            <Text size="sm" c="dimmed">
              Нет записей о сообщениях
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              Зафиксируйте переписку с организацией
            </Text>
            <Button
              variant="light"
              size="xs"
              mt="md"
              leftSection={<IconPlus size={14} />}
              onClick={handleAddMessage}
            >
              Добавить сообщение
            </Button>
          </Box>
        )}
      </BaseWidget>

      {/* Модальное окно формы */}
      <MessageFormModal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingMessage(null);
        }}
        onSubmit={handleSubmit}
        message={editingMessage}
        organizationId={organization?.id}
        loading={actionLoading}
        getTemplate={getTemplate}
      />

      {/* Модальное окно просмотра */}
      <MessageViewModal
        opened={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingMessage(null);
        }}
        message={viewingMessage}
        onEdit={handleEditMessage}
      />

      {/* Модальное окно причины редактирования */}
      <EditReasonModal
        opened={reasonModalOpen}
        onClose={() => {
          setReasonModalOpen(false);
          setPendingEditData(null);
          setModalOpen(true);
        }}
        onConfirm={handleConfirmEdit}
        loading={actionLoading}
        title="Укажите причину изменения сообщения"
        entityName="сообщение"
      />
    </>
  );
};

export default MessagesWidget;
